import { ReactNode, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { localeDate } from "../../app/datetimeUtils";
import { useAppSelector } from "../../app/hooks";
import { UserRole } from "../../app/userSlice";
import { Loading } from "../../components/Loading";
import SwitchToLegacy from "../../components/SwitchToLegacy";
import Tabs, { SubTab } from "../../components/Tabs";
import { useAverageMonthlyScoresQuery } from "../reports/admin/progress/adminProgressReportApiSlice";
import { AverageMonthlyScoresChart } from "../reports/admin/progress/AverageMonthlyScoresChart";
import { useDraftUsageOverTimeQuery } from "../reports/admin/usage/adminUsageReportApiSlice";
import { DraftUsageChart } from "../reports/admin/usage/DraftUsageChart";
import { getMaxReportLevel } from "../reports/admin/adminReportsUtil";
import { useGetTopPerformersQuery } from "../reports/admin/adminReportsApiSlice";
import { useAdminHomeQuery } from "./homeApiSlice";
import Stats from "./Stats";
import ShortcutMenu from "./ShortcutMenu";
import TopPerformersCarousel from "./TopPerformerCarousel";

export enum AdminHomeSubTabs {
	DraftUsage = "Draft Usage",
	AvgScores = "Average Scores",
}

interface AdminHomeProps {
	setAlerts: (alerts: Record<number, string> | undefined) => void;
}

export default function AdminHome(props: AdminHomeProps) {
	const { config } = useAppSelector((state) => state.site);
	const { role, district, school, isSchoolInTransition, isDistrictInTransition } = useAppSelector(
		(state) => state.user
	);
	const navigate = useNavigate();
	const maxReportLevel = getMaxReportLevel(role);

	const result = useAdminHomeQuery();
	const data = result.data;

	const draftUsageResult = useDraftUsageOverTimeQuery(
		data?.canViewReports && !isSchoolInTransition && !isDistrictInTransition
			? {
					districtId: district?.id,
					schoolId: school?.id,
				}
			: skipToken
	);
	const draftUsageData = draftUsageResult.data;

	const averageScoresResult = useAverageMonthlyScoresQuery(
		data?.canViewReports && !isSchoolInTransition && !isDistrictInTransition
			? {
					districtId: district?.id,
					schoolId: school?.id,
				}
			: skipToken
	);
	const averageScoresData = averageScoresResult.data;

	const topPerformersResult = useGetTopPerformersQuery(
		data?.canViewReports && !isSchoolInTransition && !isDistrictInTransition
			? {
					maxReportLevel: getMaxReportLevel(role),
					districtId: district?.id,
					schoolId: school?.id,
				}
			: skipToken
	);
	const topPerformers = topPerformersResult.data;

	useEffect(() => {
		props.setAlerts(data?.alerts);
	}, [data]);

	const queryString = useMemo(() => {
		const params = new URLSearchParams();

		// Super Admin and State Admin should send districtId=0 to reset the global filters by force
		if (role === UserRole.Admin || role === UserRole.StateAdmin) {
			params.append("districtId", "0");
		} else if (district?.id) {
			params.append("districtId", String(district.id));
		}

		if (school?.id) {
			params.append("schoolId", String(school.id));
		}

		return params.toString();
	}, [role, district, school]);

	const initialSubTabs: SubTab[] = useMemo(
		() => [
			{
				name: AdminHomeSubTabs.DraftUsage,
				current: true,
				content:
					draftUsageData && draftUsageData.lastUpdateDate ? (
						<AdminHomeChart
							onViewReport={() => {
								navigate(
									`/adminReports/draftUsage${queryString ? `?${queryString}` : ""}`
								);
							}}
							lastUpdateDate={draftUsageData.lastUpdateDate}>
							<DraftUsageChart
								level={maxReportLevel}
								totals={draftUsageData.response.totals}
								hasData={true}
							/>
						</AdminHomeChart>
					) : (
						<></>
					),
			},
			{
				name: AdminHomeSubTabs.AvgScores,
				current: false,
				content: averageScoresData ? (
					<AdminHomeChart
						onViewReport={() => {
							navigate(
								`/adminReports/averageScores${queryString ? `?${queryString}` : ""}`
							);
						}}
						lastUpdateDate={averageScoresData.lastUpdateDate}>
						<AverageMonthlyScoresChart
							level={maxReportLevel}
							trait={0}
							totals={averageScoresData.response.totals}
							hasData={true}
						/>
					</AdminHomeChart>
				) : (
					<></>
				),
			},
		],
		[draftUsageData, averageScoresData, maxReportLevel]
	);

	return (
		<div>
			<Stats
				label={`${maxReportLevel} Usage`}
				loading={result.isLoading}
				stats={data?.allStats ?? []}
			/>
			<div className="mt-4 flex py-2 flex-col md:flex-row">
				<div className="w-full md:w-3/4">
					{result.isLoading ||
					draftUsageResult.isLoading ||
					averageScoresResult.isLoading ||
					topPerformersResult.isLoading ? (
						<Loading loading={true} />
					) : data?.canViewReports ? (
						<div className="p-4 mt-4 bg-gray-50/10 border border-gray-200 rounded-lg">
							<p className="text-lg font-medium leading-6 text-gray-900">
								{config?.siteName} Performance and Usage
							</p>
							<div className="mt-4">
								<TopPerformersCarousel data={topPerformers ?? []} />
								<div className="mt-8">
									<Tabs
										initialSubTabs={initialSubTabs}
										ariaLabel="Admin Chart Tabs"
										containerClass="px-4"
									/>
								</div>
							</div>
						</div>
					) : (
						<div className="mt-4 px-4 py-8 bg-gray-50 border border-gray-200 rounded-lg">
							<p className="text-gray-700 text-center">
								Reports are not enabled for this {school ? "school" : "district"}.
								Please contact the MI Write team to enable reports for you.
							</p>
						</div>
					)}
				</div>
				<div className="px-4 lg:px-8 w-full md:w-1/4">
					<ShortcutMenu canSeeRosterImportShortcut={data?.canDoRosterImport} />
				</div>
			</div>
			<SwitchToLegacy link="/Home/AdminHome" />
		</div>
	);
}

interface AdminHomeChartProps {
	children: ReactNode;
	onViewReport?: () => void;
	lastUpdateDate?: Date;
}

function AdminHomeChart({ children, onViewReport, lastUpdateDate }: AdminHomeChartProps) {
	return (
		<div className="relative w-full h-96 group">
			{onViewReport && (
				<div className="z-10 absolute top-4 right-6">
					<button
						onClick={onViewReport}
						className="btn btn-naked btn-small border !shadow-md">
						<span className="flex items-center">
							View Report <ArrowRightIcon className="size-4 ml-2" />
						</span>
					</button>
				</div>
			)}
			{children}
			{lastUpdateDate && (
				<div className="absolute bottom-2 right-4">
					<span className="text-xs text-gray-500 font-medium">
						Last Updated: {localeDate(lastUpdateDate)}
					</span>
				</div>
			)}
		</div>
	);
}
